import React from "react";
import { Card } from "@/components/ui/card";
import type { IntraBrandShareBrandGroup } from "../api/transactionsDashboardApi";

interface Props {
  brands: IntraBrandShareBrandGroup[];
  isLoading: boolean;
  error: Error | null;
}

const formatWholeNumber = (value: number): string =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const formatHKD = (value: number): string =>
  `HK$ ${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const formatPercent = (value: number): string => `${value.toFixed(1)}%`;

const normalizeBrandGrouping = (value: string): string =>
  value.trim().toLowerCase() === "chaos" ? "Chaosgroup" : value;

export const IntraBrandShareCard: React.FC<Props> = ({
  brands,
  isLoading,
  error,
}) => {
  const [collapsedBrands, setCollapsedBrands] = React.useState<Set<string>>(
    () =>
      new Set([
        "Autodesk",
        "Bluebeam",
        "SketchUp",
        "Chaosgroup",
        "Adobe",
        "Others",
      ])
  );

  const toggleBrand = (brand: string) => {
    setCollapsedBrands((prev) => {
      const next = new Set(prev);
      if (next.has(brand)) {
        next.delete(brand);
      } else {
        next.add(brand);
      }
      return next;
    });
  };

  const overallRevenue = brands.reduce(
    (sum, brand) => sum + Number(brand.brand_total_revenue ?? 0),
    0
  );

  return (
    <Card className="rounded-3xl border-slate-200/70 bg-white/85 p-5 shadow-sm backdrop-blur">
      <div className="mb-4">
        <h2 className="text-xl font-semibold tracking-tight">
          Intra-Brand Share Analysis
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Grouped by 6 brands with top 3 products and "Other products".
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">
          Loading brand share data...
        </p>
      ) : error ? (
        <p className="text-sm text-rose-700">
          Failed to load data: {error.message}
        </p>
      ) : brands.every((brand) => brand.items.length === 0) ? (
        <p className="text-sm text-muted-foreground">No data available.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200/80">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-[0.12em] text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Brand / Product Portfolio</th>
                <th className="px-4 py-3 text-right">Units Sold</th>
                <th className="px-4 py-3 text-right">Revenue (HKD)</th>
                <th className="px-4 py-3 text-right">
                  Contribution To Brand (%)
                </th>
              </tr>
            </thead>
            <tbody>
              {brands.map((brand) => {
                const brandName = normalizeBrandGrouping(brand.brand_grouping);
                const brandTotal = Number(brand.brand_total_revenue ?? 0);
                const distributedPct =
                  overallRevenue > 0 ? (brandTotal / overallRevenue) * 100 : 0;
                const isCollapsed = collapsedBrands.has(brandName);

                return (
                  <React.Fragment key={brandName}>
                    <tr className="bg-slate-100/80">
                      <td
                        colSpan={3}
                        className="px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-700"
                      >
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-700"
                          onClick={() => toggleBrand(brandName)}
                        >
                          <span>{isCollapsed ? "▸" : "▾"}</span>
                          <span>{brandName}</span>
                        </button>{" "}
                        <span className="ml-2 font-normal text-slate-500">
                          Total brand billing: {formatHKD(brandTotal)}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center justify-end gap-3">
                          <span className="text-xs font-medium text-slate-500">
                            Overall distributed:
                          </span>
                          <div className="h-2 w-32 rounded-full bg-slate-200">
                            <div
                              className="h-2 rounded-full bg-rose-500"
                              style={{
                                width: `${Math.max(0, Math.min(100, distributedPct))}%`,
                              }}
                            />
                          </div>
                          <span className="w-12 text-right tabular-nums text-xs font-semibold text-slate-800">
                            {formatPercent(distributedPct)}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {isCollapsed ? null : brand.items.length === 0 ? (
                      <tr className="border-t border-slate-200/80">
                        <td colSpan={4} className="px-4 py-3 text-sm text-slate-500">
                          No products in this brand group.
                        </td>
                      </tr>
                    ) : (
                      brand.items.map((item) => {
                        const qty = Number(item.total_quantity ?? 0);
                        const revenue = Number(item.total_revenue ?? 0);
                        const share = Math.max(
                          0,
                          Math.min(100, Number(item.contribution_pct ?? 0))
                        );

                        return (
                          <tr
                            key={`${brandName}-${item.product_model}`}
                            className="border-t border-slate-200/80"
                          >
                            <td className="px-4 py-3 text-slate-800">
                              {item.product_model}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                              {formatWholeNumber(qty)}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">
                              {formatHKD(revenue)}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-3">
                                <div className="h-2 w-32 rounded-full bg-slate-200">
                                  <div
                                    className="h-2 rounded-full bg-indigo-500"
                                    style={{ width: `${share}%` }}
                                  />
                                </div>
                                <span className="w-12 text-right tabular-nums font-semibold text-slate-800">
                                  {formatPercent(share)}
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default IntraBrandShareCard;
